/**
 * CollectABILITY Pricing Configuration
 *
 * Phase 1: Penetration Pricing (Early Stage)
 * - Competitive wallet pricing starting at $0.05 for enrich
 * - One-time pricing meets Stripe $0.50 minimum
 * - Simple 3-tier batch structure: 5-24, 25-499, 500+
 * - All pricing maintains 35%+ GP minimum
 */

import type { ProcessingType, SearchType } from '@/types';

// ============================================================================
// PRICING CONSTANTS
// ============================================================================

/**
 * One-Time Search Pricing (No wallet required, meets Stripe $0.50+ minimum)
 */
export const ONE_TIME_PRICING: Record<SearchType, number> = {
  enrich: 0.89,
  validate: 0.99,
  enrich_validate: 1.49,
  scoring: 1.99,
  scoring_enrich: 2.49,
};

/**
 * Wallet Search Pricing (Prepaid balance, 35%+ GP minimum)
 */
export const WALLET_PRICING: Record<SearchType, number> = {
  enrich: 0.05,
  validate: 0.20,
  enrich_validate: 0.25,
  scoring: 0.65,
  scoring_enrich: 0.70,
};

/**
 * Batch Processing Pricing (3-Tier Structure)
 * Tier 1: 5-24 rows (small batch premium)
 * Tier 2: 25-499 rows (standard rate)
 * Tier 3: 500+ rows (volume discount)
 */
export const BATCH_PRICING = {
  validate: {
    tier1: 0.18, // 5-24 rows
    tier2: 0.15, // 25-499 rows
    tier3: 0.12, // 500+ rows
  },
  enrich: {
    tier1: 0.04,
    tier2: 0.03,
    tier3: 0.025,
  },
  enrich_validate: {
    tier1: 0.20,
    tier2: 0.18,
    tier3: 0.15,
  },
  scoring: {
    tier1: 0.55,
    tier2: 0.48,
    tier3: 0.42,
  },
  scoring_enrich: {
    tier1: 0.60,
    tier2: 0.52,
    tier3: 0.45,
  },
} as const;

/**
 * Batch Row Tier Thresholds
 */
export const BATCH_TIERS = {
  MIN_ROWS: 5, // Minimum for batch processing
  TIER1_MIN: 5, // Small batch starts at 5
  TIER2_MIN: 25, // Standard pricing starts at 25
  TIER3_MIN: 500, // Volume discount starts at 500
} as const;

/**
 * Wallet Top-Up Options
 */
export const WALLET_TOPUP_OPTIONS = [
  { amount: 5, bonus: 0, display: '$5' },
  { amount: 10, bonus: 0, display: '$10' },
  { amount: 25, bonus: 1.25, display: '$25 (+5% bonus)' }, // 5% bonus
  { amount: 50, bonus: 4.00, display: '$50 (+8% bonus)' }, // 8% bonus
  { amount: 100, bonus: 12.00, display: '$100 (+12% bonus)' }, // 12% bonus
] as const;

/**
 * Payment Minimums
 */
export const PAYMENT_MINIMUMS = {
  STRIPE_MINIMUM: 0.50, // Stripe requires minimum $0.50 transaction
  WALLET_MINIMUM: 5.00, // Minimum wallet top-up
} as const;

// ============================================================================
// PRICING CALCULATION FUNCTIONS
// ============================================================================

/**
 * Get batch pricing tier based on row count
 */
export function getBatchTier(rowCount: number): 'tier1' | 'tier2' | 'tier3' {
  if (rowCount < BATCH_TIERS.TIER2_MIN) return 'tier1';
  if (rowCount < BATCH_TIERS.TIER3_MIN) return 'tier2';
  return 'tier3';
}

/**
 * Get batch tier display name
 */
export function getBatchTierName(rowCount: number): string {
  const tier = getBatchTier(rowCount);
  if (tier === 'tier1') return 'Small Batch (5-24 rows)';
  if (tier === 'tier2') return 'Standard (25-499 rows)';
  return 'Volume Discount (500+ rows)';
}

/**
 * Calculate batch price per row for a given processing type and row count
 */
export function getBatchPricePerRow(
  processingType: ProcessingType,
  rowCount: number
): number {
  const tier = getBatchTier(rowCount);
  return BATCH_PRICING[processingType][tier];
}

/**
 * Calculate total batch cost estimate
 */
export function calculateBatchCostEstimate(
  processingType: ProcessingType,
  rowCount: number,
  aiOverview: boolean = false
): {
  pricePerRow: number;
  estimatedTotal: number;
  tier: 'tier1' | 'tier2' | 'tier3';
  tierName: string;
} {
  const pricePerRow = getBatchPricePerRow(processingType, rowCount);
  const aiOverviewCost = aiOverview ? 0.03 : 0; // AI overview add-on
  const totalPricePerRow = pricePerRow + aiOverviewCost;

  return {
    pricePerRow: totalPricePerRow,
    estimatedTotal: rowCount * totalPricePerRow,
    tier: getBatchTier(rowCount),
    tierName: getBatchTierName(rowCount),
  };
}

/**
 * Calculate wallet search cost
 */
export function getWalletSearchCost(searchType: SearchType): number {
  return WALLET_PRICING[searchType];
}

/**
 * Calculate one-time search cost
 */
export function getOneTimeSearchCost(searchType: SearchType): number {
  return ONE_TIME_PRICING[searchType];
}

/**
 * Calculate savings when using wallet vs one-time
 */
export function calculateWalletSavings(searchType: SearchType): {
  oneTimePrice: number;
  walletPrice: number;
  savings: number;
  savingsPercent: number;
} {
  const oneTimePrice = ONE_TIME_PRICING[searchType];
  const walletPrice = WALLET_PRICING[searchType];
  const savings = oneTimePrice - walletPrice;
  const savingsPercent = Math.round((savings / oneTimePrice) * 100);

  return {
    oneTimePrice,
    walletPrice,
    savings,
    savingsPercent,
  };
}

/**
 * Calculate savings when using batch vs wallet
 */
export function calculateBatchSavings(
  processingType: ProcessingType,
  rowCount: number
): {
  walletPricePerRow: number;
  batchPricePerRow: number;
  savings: number;
  savingsPercent: number;
} {
  // Map ProcessingType to SearchType for wallet pricing comparison
  const searchTypeMap: Record<ProcessingType, SearchType> = {
    validate: 'validate',
    enrich: 'enrich',
    enrich_validate: 'enrich_validate',
    scoring: 'scoring',
    scoring_enrich: 'scoring_enrich',
  };

  const walletPricePerRow = WALLET_PRICING[searchTypeMap[processingType]];
  const batchPricePerRow = getBatchPricePerRow(processingType, rowCount);
  const savings = walletPricePerRow - batchPricePerRow;
  const savingsPercent = Math.round((savings / walletPricePerRow) * 100);

  return {
    walletPricePerRow,
    batchPricePerRow,
    savings,
    savingsPercent,
  };
}

/**
 * Get wallet top-up option details including bonus
 */
export function getWalletTopupDetails(amount: number) {
  const option = WALLET_TOPUP_OPTIONS.find(opt => opt.amount === amount);
  if (!option) {
    return { amount, bonus: 0, total: amount };
  }
  return {
    amount: option.amount,
    bonus: option.bonus,
    total: option.amount + option.bonus,
  };
}

/**
 * Check if batch row count meets minimum requirements
 */
export function meetsMinimumBatchRows(rowCount: number): boolean {
  return rowCount >= BATCH_TIERS.MIN_ROWS;
}

/**
 * Check if one-time payment meets Stripe minimum
 */
export function meetsStripeMinimum(amount: number): boolean {
  return amount >= PAYMENT_MINIMUMS.STRIPE_MINIMUM;
}

// ============================================================================
// PRICING DISPLAY HELPERS
// ============================================================================

/**
 * Format price for display
 */
export function formatPrice(amount: number): string {
  return `$${amount.toFixed(2)}`;
}

/**
 * Get pricing tier badge color
 */
export function getTierBadgeColor(tier: 'tier1' | 'tier2' | 'tier3'): string {
  if (tier === 'tier1') return 'bg-slate-100 text-slate-700';
  if (tier === 'tier2') return 'bg-blue-100 text-blue-700';
  return 'bg-green-100 text-green-700';
}

/**
 * Get discount percentage between tiers
 */
export function getTierDiscount(processingType: ProcessingType, fromTier: 'tier1' | 'tier2', toTier: 'tier2' | 'tier3'): number {
  const fromPrice = BATCH_PRICING[processingType][fromTier];
  const toPrice = BATCH_PRICING[processingType][toTier];
  const discount = ((fromPrice - toPrice) / fromPrice) * 100;
  return Math.round(discount);
}
